/**
 * ipcWebShim.js — Browser-side replacement for Electron IPC
 *
 * Exposes an `electronAPI`-compatible object (`{ invoke, on, send, ... }`)
 * that routes the channels listed in src/main/preload.js to:
 *   - fetch() for static assets (templates, CSV databases)
 *   - IndexedDB for projects + user templates (see webStorage.js)
 *   - Blob download / file picker for save-as / open-dialog
 *
 * The web build is hardwired to "demo mode": get-build-info returns
 * { isDemoBuild: true } and the TRACI lookup applies an additional
 * runtime log-normal noise to every cell (see noiseMultiplier()).
 *
 * Only loaded when window.electronAPI is absent.
 */

import {
  putProject, getProject, listProjects, deleteProject,
  putUserTemplate, getUserTemplate, listUserTemplates, deleteUserTemplate,
  putAiDraft, listAiDrafts, getAiDraft, deleteAiDraft,
  cacheGet, cachePut,
} from './webStorage';
import { ECOINVENT_SOURCE, GREET_SOURCE } from './impactSource';

// ---------------------------------------------------------------------------
// Asset base URL — honors PUBLIC_URL set at build time (CRA)
// ---------------------------------------------------------------------------

const PUBLIC_URL = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const asset = (relPath) => `${PUBLIC_URL}/${relPath.replace(/^\/+/, '')}`;

// ---------------------------------------------------------------------------
// Runtime noise — log-normal, sigma=1.0, clamped to [0.2, 5]
// Drawn fresh PER CELL PER QUERY. No seed, no session ID, no persistence.
// ---------------------------------------------------------------------------

const NOISE_SIGMA = 1.0;
const NOISE_MIN = 0.2;
const NOISE_MAX = 5.0;

function noiseMultiplier() {
  // Box-Muller transform: two uniform [0,1) samples -> one standard normal
  let u = Math.random();
  let v = Math.random();
  if (u < 1e-12) u = 1e-12;
  const z = Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  // Multiplicative log-normal with mean 1 (mu=0)
  let m = Math.exp(NOISE_SIGMA * z);
  if (m < NOISE_MIN) m = NOISE_MIN;
  if (m > NOISE_MAX) m = NOISE_MAX;
  return m;
}

// ---------------------------------------------------------------------------
// CSV parser (mirrors main.js parseTraciCsv: handles quoted fields, commas)
// ---------------------------------------------------------------------------

function parseCsv(raw) {
  const lines = raw.replace(/\r\n/g, '\n').split('\n').filter(l => l.trim().length > 0);
  if (lines.length < 2) return { header: [], rows: [] };
  const header = parseLine(lines[0]).map(s => s.trim());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const fields = parseLine(lines[i]);
    if (fields.length < header.length - 1) continue;
    const row = {};
    header.forEach((col, idx) => { row[col] = (fields[idx] || '').trim(); });
    rows.push(row);
  }
  return { header, rows };
}

function parseLine(line) {
  const fields = [];
  let current = '';
  let inQuotes = false;
  for (const ch of line) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (ch === ',' && !inQuotes) { fields.push(current); current = ''; }
    else current += ch;
  }
  fields.push(current);
  return fields;
}

// ---------------------------------------------------------------------------
// Lazy-loaded TRACI lookup (parse + cache in IndexedDB on first use)
// ---------------------------------------------------------------------------

// One lookup per impact dataset. ecoinvent is licensed, so its file is noised
// on disk and again per query; GREET is public and is served exactly as built.
const TRACI_LOOKUPS = {
  [ECOINVENT_SOURCE]: {
    csv: 'data/databases/ecoinvent_3.11_traci_lookup_noisy.csv',
    meta: 'data/databases/ecoinvent_3.11_traci_lookup_noisy_meta.json',
    cacheKey: 'traci_noisy_v1',
    noised: true,
  },
  [GREET_SOURCE]: {
    csv: 'data/databases/greet_2025_traci_lookup.csv',
    meta: 'data/databases/greet_2025_traci_lookup_meta.json',
    cacheKey: 'traci_greet_2025_v1',
    noised: false,
  },
};
const COST_CACHE_KEY = 'cost_master_v1'; // v1 was data/databases/cost_database.csv

const traciMemCache = {};
let costMemCache = null;

/**
 * Stamp identifying the dataset build currently deployed, read from the tiny
 * metadata sidecar next to the CSV. The cached copy is only reused when its
 * stamp matches, so regenerating the lookup invalidates every returning user's
 * cache automatically. Without this a browser keeps its first-ever download
 * forever — `cacheGet` does not check versions.
 */
async function fetchTraciStamp(spec) {
  try {
    const resp = await fetch(asset(spec.meta), { cache: 'no-cache' });
    if (!resp.ok) return null;
    const meta = await resp.json();
    return meta?.version || meta?.generated_at || null;
  } catch (_) {
    // Offline or sidecar missing — fall back to whatever is cached.
    return null;
  }
}

async function loadTraciLookup(source = ECOINVENT_SOURCE) {
  const spec = TRACI_LOOKUPS[source];
  if (!spec) throw new Error(`No lookup for impact dataset "${source}"`);
  if (traciMemCache[source]) return traciMemCache[source];

  const stamp = await fetchTraciStamp(spec);

  // Try IndexedDB cache first, but only if it holds the deployed build.
  const cached = await cacheGet(spec.cacheKey);
  if (cached?.payload && (stamp === null || cached.version === stamp)) {
    traciMemCache[source] = cached.payload;
    return traciMemCache[source];
  }

  // Fetch + parse
  const resp = await fetch(asset(spec.csv));
  if (!resp.ok) throw new Error(`Failed to fetch TRACI lookup: ${resp.status}`);
  const raw = await resp.text();
  const { rows } = parseCsv(raw);
  for (const row of rows) {
    row._searchText = `${row.activity_name || ''} ${row.reference_product || ''} ${row.location || ''}`.toLowerCase();
  }
  traciMemCache[source] = rows;
  // Best-effort persist; ignore failures (quota, private mode, etc.)
  // Same key each time, so a refresh replaces the entry rather than orphaning it.
  try { await cachePut(spec.cacheKey, rows, stamp || 1); } catch (_) { /* noop */ }
  return traciMemCache[source];
}

async function loadCostDb() {
  if (costMemCache) return costMemCache;
  const cached = await cacheGet(COST_CACHE_KEY);
  if (cached?.payload) {
    costMemCache = cached.payload;
    return costMemCache;
  }
  const resp = await fetch(asset('data/costs/cost_master.csv'));
  if (!resp.ok) throw new Error(`Failed to fetch cost master: ${resp.status}`);
  const raw = await resp.text();
  const { rows } = parseCsv(raw);
  for (const row of rows) {
    row.price_per_unit = parseFloat(row.price_per_unit) || 0;
    // Aliases carry the old cost_database.csv item names, so a search that
    // worked before the master landed still finds the row.
    row._searchText = [row.category, row.item, row.description, row.aliases, row.source]
      .filter(Boolean).join(' ').toLowerCase();
  }
  costMemCache = rows;
  try { await cachePut(COST_CACHE_KEY, rows, 1); } catch (_) { /* noop */ }
  return costMemCache;
}

// ---------------------------------------------------------------------------
// File picker (open / save) — pending-pick handle map for load-project
// ---------------------------------------------------------------------------

const pendingFiles = new Map(); // virtualPath -> File

function pickJsonFile() {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.style.display = 'none';
    document.body.appendChild(input);
    let resolved = false;
    const onChange = () => {
      resolved = true;
      const file = input.files && input.files[0];
      input.remove();
      if (!file) return resolve({ canceled: true, filePaths: [] });
      const virtualPath = `webfile://${Date.now()}/${file.name}`;
      pendingFiles.set(virtualPath, file);
      resolve({ canceled: false, filePaths: [virtualPath] });
    };
    input.addEventListener('change', onChange, { once: true });
    // Heuristic cancel detection: focusin without change → user cancelled
    const onFocus = () => {
      setTimeout(() => {
        if (!resolved) {
          input.remove();
          resolve({ canceled: true, filePaths: [] });
        }
      }, 500);
      window.removeEventListener('focus', onFocus);
    };
    window.addEventListener('focus', onFocus);
    input.click();
  });
}

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 100);
}

// ---------------------------------------------------------------------------
// Project storage key
// ---------------------------------------------------------------------------

// A project is stored under its project_id (see projectIdentity.js), so projects
// with the same name keep separate entries. Projects saved before ids existed
// are keyed by their name; 'load-project' hands that key back as the id, so they
// keep saving over the same entry. The name fallback only covers a caller that
// passes no id.
function storageKeyFor(projectData) {
  const key = projectData?.project_id || projectData?.project_info?.name || projectData?.project_name || 'untitled';
  return String(key).replace(/[^a-zA-Z0-9_-]/g, '_');
}

// ---------------------------------------------------------------------------
// Channel handlers
// ---------------------------------------------------------------------------

const handlers = {
  // -- Build info --
  'get-build-info': async () => ({ success: true, isDemoBuild: true }),

  // -- DB mode (locked to noisy in web) --
  'get-traci-db-mode': async () => ({ success: true, mode: 'noisy' }),
  'set-traci-db-mode': async () => ({ success: true, mode: 'noisy' }),

  // -- Templates --
  // `{ builtin: true }` skips the customized copy and returns the shipped file.
  'load-template': async (templateName, { builtin = false } = {}) => {
    // Check user templates first (matches desktop behavior)
    const user = builtin ? null : await getUserTemplate(templateName);
    if (user) return { ...user, _source: 'user' };
    const resp = await fetch(asset(`data/templates/${templateName}`));
    if (!resp.ok) throw new Error(`Failed to load template: ${templateName}`);
    return await resp.json();
  },

  'save-user-template': async (templateName, templateData) => {
    await putUserTemplate(templateName, templateData);
    return { success: true, filePath: `idb://userTemplates/${templateName}` };
  },

  'list-user-templates': async () => listUserTemplates(),

  'delete-user-template': async (templateName) => {
    try {
      await deleteUserTemplate(templateName);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // -- CSV templates --
  'list-csv-templates': async () => {
    const resp = await fetch(asset('data/templates/csv/index.json'));
    if (!resp.ok) return [];
    return await resp.json();
  },

  'get-csv-template': async (filename) => {
    const resp = await fetch(asset(`data/templates/csv/${filename}`));
    if (!resp.ok) throw new Error(`Failed to fetch CSV template: ${filename}`);
    return await resp.text();
  },

  // -- TRACI lookup (with runtime noise) --
  // `source` picks the dataset (defaults to ecoinvent). The third argument is
  // the desktop app's noisy-DB flag; the web build decides noise per dataset.
  'search-traci-lookup': async (query, maxResults = 50, _useNoisy, source = ECOINVENT_SOURCE) => {
    try {
      const spec = TRACI_LOOKUPS[source] || TRACI_LOOKUPS[ECOINVENT_SOURCE];
      const lookup = await loadTraciLookup(TRACI_LOOKUPS[source] ? source : ECOINVENT_SOURCE);
      if (!query || query.trim().length < 2) {
        return { success: true, results: [], total: lookup.length };
      }
      const terms = query.toLowerCase().split(/\s+/).filter(t => t.length > 0);
      const matches = [];
      for (const row of lookup) {
        if (terms.every(t => row._searchText.includes(t))) {
          // Apply runtime per-cell log-normal noise (per-query, ephemeral) to
          // licensed data only.
          const noisify = spec.noised ? (v) => (v || 0) * noiseMultiplier() : (v) => v || 0;
          matches.push({
            activity_name: row.activity_name,
            reference_product: row.reference_product,
            reference_product_unit: row.reference_product_unit || '',
            location: row.location,
            traci_scores: {
              acidification: noisify(parseFloat(row.traci_acidification) || 0),
              climate_change: noisify(parseFloat(row.traci_climate_change) || 0),
              ecotoxicity_freshwater: noisify(parseFloat(row.traci_ecotoxicity_freshwater) || 0),
              eutrophication: noisify(parseFloat(row.traci_eutrophication) || 0),
              human_toxicity_carcinogenic: noisify(parseFloat(row.traci_human_toxicity_carcinogenic) || 0),
              human_toxicity_non_carcinogenic: noisify(parseFloat(row['traci_human_toxicity_non-carcinogenic']) || 0),
              ozone_depletion: noisify(parseFloat(row.traci_ozone_depletion) || 0),
              particulate_matter: noisify(parseFloat(row.traci_particulate_matter_formation) || 0),
              photochemical_oxidant: noisify(parseFloat(row.traci_photochemical_oxidant_formation) || 0),
            },
          });
          if (matches.length >= maxResults) break;
        }
      }
      return { success: true, results: matches, total: lookup.length };
    } catch (err) {
      console.error('[WebShim] TRACI search failed:', err);
      return { success: false, error: err.message, results: [] };
    }
  },

  // -- Cost DB --
  'search-cost-database': async (query, maxResults = 30) => {
    try {
      const db = await loadCostDb();
      if (!query || query.trim().length < 1) {
        return { success: true, results: db.slice(0, maxResults), total: db.length };
      }
      const terms = query.toLowerCase().split(/\s+/).filter(t => t.length > 0);
      const matches = [];
      for (const row of db) {
        if (terms.every(t => row._searchText.includes(t))) {
          // eslint-disable-next-line no-unused-vars
          const { _searchText, ...clean } = row;
          matches.push(clean);
          if (matches.length >= maxResults) break;
        }
      }
      return { success: true, results: matches, total: db.length };
    } catch (err) {
      console.error('[WebShim] Cost search failed:', err);
      return { success: false, error: err.message, results: [] };
    }
  },

  // -- Project IO (IndexedDB) --
  'auto-save-project': async (projectData) => {
    try {
      const key = storageKeyFor(projectData);
      await putProject(key, projectData);
      return { success: true, filePath: `idb://projects/${key}` };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  'save-project': async (filePath, projectData) => {
    // In web, manual save = download only. IDB persistence is handled by auto-save.
    try {
      const name = projectData?.project_info?.name || projectData?.project_name || 'untitled';
      const safe = name.replace(/[^a-zA-Z0-9_\- ]/g, '_');
      const fileName = `${safe}.json`;
      downloadJson(fileName, projectData);
      return { success: true, filePath: `download://${fileName}` };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  'save-project-as': async (projectData) => {
    // In web, manual save = download only. IDB persistence is handled by auto-save.
    try {
      const name = projectData?.project_info?.name || projectData?.project_name || 'untitled';
      const safe = name.replace(/[^a-zA-Z0-9_\- ]/g, '_');
      const fileName = `${safe}.json`;
      downloadJson(fileName, projectData);
      return { success: true, filePath: `download://${fileName}` };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  'load-project': async (filePath) => {
    // Two cases:
    //   1) virtualPath from show-open-dialog — read from pending file picker
    //   2) idb://projects/<key> — read from IndexedDB
    if (filePath?.startsWith('webfile://')) {
      const file = pendingFiles.get(filePath);
      pendingFiles.delete(filePath);
      if (!file) throw new Error('File handle expired; please pick again.');
      const text = await file.text();
      const project = JSON.parse(text);
      if (Array.isArray(project.processes)) {
        const obj = {};
        project.processes.forEach(p => {
          const id = p.process_id || p.id;
          obj[id] = { ...p, id };
        });
        project.processes = obj;
      }
      return project;
    }
    if (filePath?.startsWith('idb://projects/')) {
      const key = filePath.replace('idb://projects/', '');
      const project = await getProject(key);
      if (!project) throw new Error(`Project not found: ${key}`);
      // Saving it again must land on the entry it came from (older entries have no id).
      return { ...project, project_id: key };
    }
    throw new Error(`Unsupported file path in web demo: ${filePath}`);
  },

  'list-saved-projects': async () => listProjects(),

  'delete-saved-project': async (filePath) => {
    if (!filePath?.startsWith('idb://projects/')) {
      return { success: false, error: `Not a browser-saved project: ${filePath}` };
    }
    try {
      await deleteProject(filePath.replace('idb://projects/', ''));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // -- AI Builder drafts --
  'save-ai-draft': async (draftId, templateData) => {
    try {
      await putAiDraft(draftId, templateData);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  'list-ai-drafts': async () => {
    try {
      return await listAiDrafts();
    } catch (err) {
      return [];
    }
  },

  'get-ai-draft': async (draftId) => {
    try {
      return await getAiDraft(draftId);
    } catch (err) {
      return null;
    }
  },

  'delete-ai-draft': async (draftId) => {
    try {
      await deleteAiDraft(draftId);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // -- Dialogs --
  'show-open-dialog': async () => pickJsonFile(),

  'show-save-dialog': async () => {
    // Web has no save-path concept; renderer should call save-project-as instead.
    return { canceled: false, filePath: '' };
  },
};

// ---------------------------------------------------------------------------
// Public API — matches Electron's contextBridge.exposeInMainWorld('electronAPI')
// ---------------------------------------------------------------------------

export function createWebElectronAPI() {
  return {
    invoke: async (channel, ...args) => {
      const handler = handlers[channel];
      if (!handler) {
        return Promise.reject(new Error(`IPC channel not implemented in web shim: ${channel}`));
      }
      return handler(...args);
    },
    send: () => { /* no-op in web */ },
    on: () => () => { /* no menu events in web; return no-op unsubscribe */ },
    removeAllListeners: () => { /* no-op */ },
    removeListener: () => { /* no-op */ },
  };
}

export default createWebElectronAPI;
